import { Controller } from "@hotwired/stimulus"

// Submits the "Automatische Tageseinträge" config card via fetch. The card is
// rendered inside the page-wide template form, where a nested <form> tag would
// be dropped by the HTML parser — so there is no form element at all (same
// pattern as default_site_form_controller). The active periods are rows of
// nameless inputs, tagged by data-period-field, so the surrounding template
// form never submits them either.
const PERIOD_FIELDS = ["start_day", "start_month", "end_day", "end_month"]

export default class extends Controller {
  static targets = [
    "enabled",
    "hour",
    "periods",
    "period",
    "periodTemplate",
    "shortTimeWorkSalaryTypeNumber",
    "shortTimeWorkCleanupDays",
  ]
  static values = { url: String, errorMessage: String }

  // Clones the empty row; the S-Kug preset button passes its boundaries as
  // action params (data-auto-daily-entries-form-start-day-param etc.). Values
  // are set before insertion, so SlimSelect picks up the chosen month on connect.
  addPeriod(event) {
    const row = this.periodTemplateTarget.content.firstElementChild.cloneNode(true)
    const params = event.params || {}
    const presets = {
      start_day: params.startDay,
      start_month: params.startMonth,
      end_day: params.endDay,
      end_month: params.endMonth,
    }

    PERIOD_FIELDS.forEach((field) => {
      if (presets[field] !== undefined) {
        row.querySelector(`[data-period-field="${field}"]`).value = String(presets[field])
      }
    })

    this.periodsTarget.appendChild(row)
  }

  removePeriod(event) {
    event.currentTarget.closest("[data-auto-daily-entries-form-target='period']").remove()
  }

  periodValues() {
    return this.periodTargets.map((row) =>
      Object.fromEntries(
        PERIOD_FIELDS.map((field) => [
          field,
          row.querySelector(`[data-period-field="${field}"]`).value,
        ]),
      ),
    )
  }

  // Saison-Kurzarbeitergeld: a blank Lohnart switches it off. Omitted when the
  // card has no S-Kug inputs (the tenant has none to render), so the server keeps
  // what is stored.
  shortTimeWorkValues() {
    if (!this.hasShortTimeWorkSalaryTypeNumberTarget) return {}

    return {
      short_time_work_salary_type_number_id: this.shortTimeWorkSalaryTypeNumberTarget.value,
      short_time_work_cleanup_days: this.shortTimeWorkCleanupDaysTarget.value,
    }
  }

  async save(event) {
    event.preventDefault()

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
        body: JSON.stringify({
          enabled: this.enabledTarget.checked,
          hour: this.hourTarget.value,
          periods: this.periodValues(),
          ...this.shortTimeWorkValues(),
        }),
      })

      // Validation errors come back as a turbo stream too (re-rendered card +
      // flash), so render any stream body instead of treating non-2xx as fatal.
      if (response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(await response.text())
      } else if (!response.ok) {
        throw new Error("Update failed")
      }
    } catch (error) {
      console.error("Error updating auto daily entries:", error)
      // The view always supplies the translated message; never fall back to an
      // English literal in a German UI — stay silent rather than leak one.
      if (this.errorMessageValue) alert(this.errorMessageValue)
    }
  }
}
